import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import Select, { components } from "react-select";
import Flatpickr from "react-flatpickr";
import { Spinner } from "reactstrap";
import { useMatch, useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEditPayment = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const isView = !!useMatch("/payment/view/:id");
    const isEdit = !!useMatch("/payment/edit/:id");
    const isCreate = !id;

    const getToday = () => {
        const d = new Date();
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const [loading, setLoading] = useState(false);
    const [students, setStudents] = useState([]);
    const [studentPaymentId, setStudentPaymentId] = useState(0);
    const [studentPayment, setStudentPayment] = useState(0);
    const [paymentDate, setPaymentDate] = useState(getToday());
    const [dateError, setDateError] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [transactionRefNo, setTransactionRefNo] = useState("");
    const [notes, setNotes] = useState("");
    const [isCustomAmount, setIsCustomAmount] = useState(false);
    const [isFeesPending, setIsFeesPending] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState([]);
    const [multiPayments, setMultiPayments] = useState([]);

    const token = localStorage.getItem('authToken');
    const user = JSON.parse(localStorage.getItem("authUser"));

    const mysqlToDDMMYYYY = (dateStr) => {
        if (!dateStr) return "";

        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
    };

    const fetchData = useCallback(async () => {
        try {
            const fetchList = async (url) => {
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json();
                return json?.recordsets?.[0] || [];
            };

            const url = isCreate
                ? "https://api.kunalsdrumclass.com/api/GetStudentPayments"
                : "https://api.kunalsdrumclass.com/api/GetActiveStudents";

            const data = await fetchList(url);
            setStudents(data);

        } catch (error) {
            console.error("Fetch error", error);
        }
    }, [isCreate, token]);

    const loadDetails = useCallback(async () => {
        if (!id) return;

        try {
            const res = await fetch(`https://api.kunalsdrumclass.com/api/GetStudentPaymentTransactionById?id=${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            const item = json?.recordsets?.[0]?.[0];

            setSelectedStudent([item.Student_Id]);
            setStudentPayment(item.Total_Amount);
            setStudentPaymentId(item.Student_Payment_Id);
            setPaymentDate(mysqlToDDMMYYYY(item.Payment_Date));
            setPaymentMode(item.Payment_Mode);
            setTransactionRefNo(item.Transaction_Ref_No);
            setNotes(item.Notes);
            setIsCustomAmount(true);
            setIsFeesPending(item.IsFeesPending);
        } catch (err) {
            console.error(err);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (id) loadDetails();
    }, [id, loadDetails]);

    const getStudentDetails = async (studentId) => {
        try {
            const res = await fetch(`https://api.kunalsdrumclass.com/api/GetStudentPaymentIdByStudentId?studentId=${studentId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            const item = json?.recordsets?.[0]?.[0];
            setStudentPaymentId(item.StudentPaymentId);

        } catch (error) {
            console.error("Error fetching student details:", error);
        }
    };

    const getStudentPaymentDetails = async (id) => {
        try {
            const res = await fetch(`https://api.kunalsdrumclass.com/api/GetStudentTotalWithAdvanceByStudentId?id=${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            const item = json?.recordsets?.[0]?.[0];
            setStudentPayment(item.Total_With_Advance);

        } catch (error) {
            console.error("Error fetching student details:", error);
        }
    };

    const getClassAmount = (classDuration) => {
        const duration = Number(classDuration);

        if (duration === 1) return 2500;
        if (duration === 3) return 7500;

        return 0;
    };

    const getStudentFullDetails = async (studentIds) => {
        try {
            const promises = studentIds.map(async (studentId) => {
                const res = await fetch(
                    `https://api.kunalsdrumclass.com/api/GetStudentPaymentIdByStudentId?studentId=${studentId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const json = await res.json();
                const paymentItem = json?.recordsets?.[0]?.[0];

                if (paymentItem) {
                    return {
                        studentId,
                        studentPaymentId: paymentItem.StudentPaymentId,
                    };
                }

                return null;
            });

            const results = await Promise.all(promises);

            setMultiPayments(results.filter(Boolean));
        } catch (error) {
            console.error("Error fetching student details:", error);
        }
    };

    const processMultiplePayments = async (mysqlDate) => {
        let successCount = 0;
        let failedCount = 0;

        const failedStudents = [];
        const totalPayments = multiPayments.length;

        if (totalPayments === 0) {
            await Swal.fire({
                title: "No Students Selected",
                text: "Please select at least one student.",
                icon: "warning",
                customClass: {
                    popup: "swal2-material-ui",
                    confirmButton: "btn btn-sm rounded-8 btn-warning",
                },
            });

            return;
        }

        const updateProgressPopup = (
            completed,
            currentStudent = "",
            statusText = "Processing payment..."
        ) => {
            const percentage = Math.round(
                (completed / totalPayments) * 100
            );

            const progressBar = Swal.getPopup()?.querySelector(
                "#payment-progress-bar"
            );

            const progressText = Swal.getPopup()?.querySelector(
                "#payment-progress-text"
            );

            const currentStudentText = Swal.getPopup()?.querySelector(
                "#current-student"
            );

            const status = Swal.getPopup()?.querySelector(
                "#payment-status"
            );

            if (progressBar) {
                progressBar.style.width = `${percentage}%`;
            }

            if (progressText) {
                progressText.innerHTML = `
                <strong>${completed}</strong> out of
                <strong>${totalPayments}</strong> done
            `;
            }

            if (currentStudentText) {
                currentStudentText.innerHTML = currentStudent
                    ? `Student: <strong>${currentStudent}</strong>`
                    : "";
            }

            if (status) {
                status.innerHTML = statusText;
            }
        };

        Swal.fire({
            title: "Processing Payments",
            html: `
            <div class="text-center">

                <div
                    style="
                        font-size: 18px;
                        margin-bottom: 15px;
                        color: #495057;
                    "
                    id="payment-progress-text"
                >
                    <strong>0</strong> out of
                    <strong>${totalPayments}</strong> done
                </div>

                <div
                    style="
                        width: 100%;
                        height: 12px;
                        background: #e9ecef;
                        border-radius: 10px;
                        overflow: hidden;
                        margin-bottom: 15px;
                    "
                >
                    <div
                        id="payment-progress-bar"
                        style="
                            width: 0%;
                            height: 100%;
                            background: #556ee6;
                            transition: width 0.3s ease;
                        "
                    ></div>
                </div>

                <div
                    id="current-student"
                    style="
                        font-size: 14px;
                        color: #495057;
                        margin-bottom: 8px;
                    "
                ></div>

                <div
                    id="payment-status"
                    style="
                        font-size: 13px;
                        color: #74788d;
                    "
                >
                    Starting payment process...
                </div>

            </div>
        `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            customClass: {
                popup: "swal2-material-ui",
            },
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        for (let index = 0; index < multiPayments.length; index++) {
            const item = multiPayments[index];

            const student = students.find(
                s => s.Id === item.studentId
            );

            const studentName =
                student?.Student_Name ||
                student?.Name ||
                `Student #${item.studentId}`;

            const totalAmount = isCustomAmount
                ? Number(studentPayment)
                : getClassAmount(student?.Class_Duration);

            const completed = index;

            updateProgressPopup(
                completed,
                studentName,
                `Processing payment ${index + 1}...`
            );

            let paymentCompleted = false;

            try {
                const validatePayload = {
                    studentPaymentId: item.studentPaymentId,
                    totalNewAmount: totalAmount,
                };

                const validateRes = await fetch(
                    "https://api.kunalsdrumclass.com/api/ValidateStudentPayments",
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(validatePayload),
                    }
                );

                const validateData = await validateRes.json();

                const status =
                    validateData?.recordsets?.[0]?.[0]?.Status;

                if (status === 1) {
                    failedCount++;

                    failedStudents.push({
                        studentName,
                        reason: "Student payment already exists",
                    });

                    Swal.close();

                    const result = await Swal.fire({
                        title: "Payment Already Exists",
                        html: `
                        <div>
                            <p>
                                Payment already exists for:
                            </p>

                            <strong>${studentName}</strong>

                            <p class="mt-2">
                                Progress:
                                <strong>
                                    ${index + 1} / ${totalPayments}
                                </strong>
                            </p>
                        </div>
                    `,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Skip & Continue",
                        cancelButtonText: "Stop Queue",
                        customClass: {
                            popup: "swal2-material-ui",
                            confirmButton:
                                "btn btn-sm rounded-8 btn-warning",
                            cancelButton:
                                "btn btn-sm rounded-8 btn-secondary",
                        },
                    });

                    if (!result.isConfirmed) {
                        break;
                    }

                    paymentCompleted = true;

                    Swal.fire({
                        title: "Processing Payments",
                        html: `
                        <div class="text-center">

                            <div
                                style="
                                    font-size: 18px;
                                    margin-bottom: 15px;
                                "
                                id="payment-progress-text"
                            >
                                <strong>${index + 1}</strong> out of
                                <strong>${totalPayments}</strong> done
                            </div>

                            <div
                                style="
                                    width: 100%;
                                    height: 12px;
                                    background: #e9ecef;
                                    border-radius: 10px;
                                    overflow: hidden;
                                    margin-bottom: 15px;
                                "
                            >
                                <div
                                    id="payment-progress-bar"
                                    style="
                                        width: ${Math.round(
                            ((index + 1) / totalPayments) * 100
                        )}%;
                                        height: 100%;
                                        background: #556ee6;
                                        transition: width 0.3s ease;
                                    "
                                ></div>
                            </div>

                            <div
                                id="current-student"
                                style="
                                    font-size: 14px;
                                    color: #495057;
                                    margin-bottom: 8px;
                                "
                            >
                                Skipped:
                                <strong>${studentName}</strong>
                            </div>

                            <div
                                id="payment-status"
                                style="
                                    font-size: 13px;
                                    color: #74788d;
                                "
                            >
                                Continuing...
                            </div>

                        </div>
                    `,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        showConfirmButton: false,
                        customClass: {
                            popup: "swal2-material-ui",
                        },
                    });

                    continue;
                }

                const insertPayload = {
                    id: 0,
                    studentPaymentId: item.studentPaymentId,
                    paymentDate: mysqlDate,
                    totalAmount: totalAmount,
                    paymentMode: paymentMode,
                    isFeesPending: isFeesPending,
                    userId: user.Id,
                };

                const insertRes = await fetch(
                    "https://api.kunalsdrumclass.com/api/InsertUpdateStudentPaymentTransaction",
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(insertPayload),
                    }
                );

                const insertData = await insertRes.json();

                const insertStatus =
                    insertData?.recordsets?.[0]?.[0]?.Id;

                if (insertStatus === 1) {
                    failedCount++;

                    failedStudents.push({
                        studentName,
                        reason: "Amount is greater than pending amount",
                    });

                    Swal.close();

                    const result = await Swal.fire({
                        title: "Invalid Amount",
                        html: `
                        <div>
                            <p>
                                Amount entered is greater than the
                                pending amount for:
                            </p>

                            <strong>${studentName}</strong>

                            <p class="mt-2">
                                Progress:
                                <strong>
                                    ${index + 1} / ${totalPayments}
                                </strong>
                            </p>
                        </div>
                    `,
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Skip & Continue",
                        cancelButtonText: "Stop Queue",
                        customClass: {
                            popup: "swal2-material-ui",
                            confirmButton:
                                "btn btn-sm rounded-8 btn-warning",
                            cancelButton:
                                "btn btn-sm rounded-8 btn-secondary",
                        },
                    });

                    if (!result.isConfirmed) {
                        break;
                    }

                    paymentCompleted = true;

                    Swal.fire({
                        title: "Processing Payments",
                        html: `
                        <div class="text-center">

                            <div
                                style="
                                    font-size: 18px;
                                    margin-bottom: 15px;
                                "
                                id="payment-progress-text"
                            >
                                <strong>${index + 1}</strong> out of
                                <strong>${totalPayments}</strong> done
                            </div>

                            <div
                                style="
                                    width: 100%;
                                    height: 12px;
                                    background: #e9ecef;
                                    border-radius: 10px;
                                    overflow: hidden;
                                    margin-bottom: 15px;
                                "
                            >
                                <div
                                    id="payment-progress-bar"
                                    style="
                                        width: ${Math.round(
                            ((index + 1) / totalPayments) * 100
                        )}%;
                                        height: 100%;
                                        background: #556ee6;
                                        transition: width 0.3s ease;
                                    "
                                ></div>
                            </div>

                            <div
                                id="current-student"
                                style="
                                    font-size: 14px;
                                    color: #495057;
                                "
                            >
                                Skipped:
                                <strong>${studentName}</strong>
                            </div>

                            <div
                                id="payment-status"
                                style="
                                    font-size: 13px;
                                    color: #74788d;
                                    margin-top: 8px;
                                "
                            >
                                Continuing...
                            </div>

                        </div>
                    `,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        showConfirmButton: false,
                        customClass: {
                            popup: "swal2-material-ui",
                        },
                    });

                    continue;
                }

                successCount++;
                paymentCompleted = true;

            } catch (error) {
                console.error(
                    `Error processing payment for ${studentName}`,
                    error
                );

                failedCount++;

                failedStudents.push({
                    studentName,
                    reason: "API error",
                });

                Swal.close();

                const result = await Swal.fire({
                    title: "Error Processing Payment",
                    html: `
                    <div>
                        <p>
                            An error occurred while processing:
                        </p>

                        <strong>${studentName}</strong>

                        <p class="mt-2">
                            Progress:
                            <strong>
                                ${index + 1} / ${totalPayments}
                            </strong>
                        </p>
                    </div>
                `,
                    icon: "error",
                    showCancelButton: true,
                    confirmButtonText: "Skip & Continue",
                    cancelButtonText: "Stop Queue",
                    customClass: {
                        popup: "swal2-material-ui",
                        confirmButton:
                            "btn btn-sm rounded-8 btn-warning",
                        cancelButton:
                            "btn btn-sm rounded-8 btn-secondary",
                    },
                });

                if (!result.isConfirmed) {
                    break;
                }

                paymentCompleted = true;

                Swal.fire({
                    title: "Processing Payments",
                    html: `
                    <div class="text-center">

                        <div
                            style="
                                font-size: 18px;
                                margin-bottom: 15px;
                            "
                            id="payment-progress-text"
                        >
                            <strong>${index + 1}</strong> out of
                            <strong>${totalPayments}</strong> done
                        </div>

                        <div
                            style="
                                width: 100%;
                                height: 12px;
                                background: #e9ecef;
                                border-radius: 10px;
                                overflow: hidden;
                                margin-bottom: 15px;
                            "
                        >
                            <div
                                id="payment-progress-bar"
                                style="
                                    width: ${Math.round(
                        ((index + 1) / totalPayments) * 100
                    )}%;
                                    height: 100%;
                                    background: #556ee6;
                                "
                            ></div>
                        </div>

                        <div
                            id="current-student"
                            style="
                                font-size: 14px;
                                color: #495057;
                            "
                        >
                            Skipped:
                            <strong>${studentName}</strong>
                        </div>

                        <div
                            id="payment-status"
                            style="
                                font-size: 13px;
                                color: #74788d;
                                margin-top: 8px;
                            "
                        >
                            Continuing...
                        </div>

                    </div>
                `,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    customClass: {
                        popup: "swal2-material-ui",
                    },
                });
            }

            const completedCount = index + 1;

            if (Swal.isVisible()) {
                updateProgressPopup(
                    completedCount,
                    studentName,
                    paymentCompleted
                        ? "Payment completed. Continuing..."
                        : "Processing..."
                );
            }

            console.log(
                `Payment ${completedCount}/${totalPayments} completed for ${studentName}`
            );

            await new Promise(resolve => setTimeout(resolve, 300));
        }

        Swal.close();

        let failedHtml = "";

        if (failedStudents.length > 0) {
            failedHtml = `
            <div class="mt-3 text-start">
                <strong>Failed / Skipped:</strong>

                <ul>
                    ${failedStudents
                    .map(
                        item => `
                                <li>
                                    ${item.studentName}
                                    - ${item.reason}
                                </li>
                            `
                    )
                    .join("")}
                </ul>
            </div>
        `;
        }

        await Swal.fire({
            title: "Queue Completed",
            html: `
            <div>
                <p>
                    <strong>${successCount}</strong>
                    payment(s) created successfully.
                </p>

                <p>
                    <strong>${failedCount}</strong>
                    payment(s) failed/skipped.
                </p>

                ${failedHtml}
            </div>
        `,
            icon: failedCount > 0 ? "warning" : "success",
            customClass: {
                popup: "swal2-material-ui",
                confirmButton:
                    "btn btn-sm rounded-8 btn-primary",
            },
        });

        navigate(-1);
    };

    const handleSave = async () => {
        try {
            setDateError("");

            if (!paymentDate) {
                setDateError("Please fill in this field.");
                return;
            }

            const [d, m, y] = paymentDate.split("-");
            const mysqlDate = `${y}-${m}-${d}`;

            if (selectedStudent.length === 1) {
                setLoading(true);
                
                const selectedStudentData = students.find(
                    student => student.Id === selectedStudent[0]
                );

                const totalAmount = isCustomAmount
                    ? Number(studentPayment)
                    : getClassAmount(selectedStudentData?.Class_Duration);

                const payload = {
                    id: isEdit ? id : 0,
                    studentPaymentId: studentPaymentId,
                    paymentDate: mysqlDate,
                    totalAmount: totalAmount,
                    paymentMode: paymentMode,
                    transactionRefNo: transactionRefNo,
                    notes: notes,
                    isFeesPending: isFeesPending,
                    userId: user.Id,
                };

                const res = await fetch("https://api.kunalsdrumclass.com/api/InsertUpdateStudentPaymentTransaction", {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const data = await res.json();
                const status = data?.recordsets?.[0]?.[0]?.Id;

                if (status === 1) {
                    await Swal.fire({
                        title: "Invalid Amount Entered",
                        text: "The entered amount is greater than the pending amount. Please enter a valid amount.",
                        icon: "warning",
                        customClass: {
                            popup: "swal2-material-ui",
                            confirmButton: "btn btn-sm rounded-8 btn-warning",
                        },
                    });

                    return;
                }

                await Swal.fire({
                    title: "Success!",
                    text: isEdit ? "Payment updated successfully!" : "Payment created successfully!",
                    icon: "success",
                    customClass: {
                        popup: "swal2-material-ui",
                        confirmButton: "btn btn-sm rounded-8 btn-primary",
                    },
                });

                navigate(-1);
                return;
            }

            await processMultiplePayments(mysqlDate);

        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong while saving payment", "error");
        } finally {
            setLoading(false);
        }
    };

    const selectedClassDuration = selectedStudent.length > 0
        ? students.find(
            student => student.Id === selectedStudent[0]
        )?.Class_Duration
        : null;

    const studentOptions = students
        .filter(item => {
            if (!isCustomAmount) {
                return true;
            }

            if (!selectedClassDuration) {
                return true;
            }

            return Number(item.Class_Duration) === Number(selectedClassDuration);
        })
        .map(item => ({
            value: item.Id,

            searchText: [
                item.Student_Name,
                item.Name,
                item.Class_Duration
                    ? `${item.Class_Duration} Month`
                    : "",
                item.Payment_Type
            ]
                .filter(Boolean)
                .join(" "),

            label: (
                <>
                    {`${item.Student_Name || item.Name || "Unknown"}${item.Class_Duration
                        ? ` (${item.Class_Duration} Month)`
                        : ""
                        }`}

                    {item.Payment_Type === "Midway" && (
                        <span className="badge bg-warning ms-2">
                            Midway
                        </span>
                    )}
                </>
            )
        }));

    const paymentOptions = [
        { value: "Cash", label: "Cash" },
        { value: "Kunal (ICICI Savings)", label: "Kunal (ICICI Savings)" },
        { value: "Poornima (CCBL)", label: "Poornima (CCBL)" },
        { value: "Poornima (HDFC)", label: "Poornima (HDFC)" },
        { value: "Kunal (HUF)", label: "Kunal (HUF)" },
        { value: "KDC (ICICI Current)", label: "KDC (ICICI Current)" },
    ];

    const Option = (props) => {
        return (
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    style={{ marginRight: 8 }}
                />
                {props.label}
            </components.Option>
        );
    };

    const filterStudentOption = (option, inputValue) => {
        return option.data.searchText
            .toLowerCase()
            .includes(inputValue.toLowerCase());
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title="Payments"
                    breadcrumbItems={[
                        { title: "Payments", link: "/payments" },
                        { title: isCreate ? "Create Payment" : isView ? "View Payment" : "Edit Payment" }
                    ]}
                />
            </div>

            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">
                        {isCreate && "Create Payment"}
                        {isView && "View Payment"}
                        {isEdit && "Edit Payment"}
                    </h3>

                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                    >
                        Back to List
                    </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    {loading ? (
                        <div className="text-center my-4">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <>
                            {/* Form */}
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-12 mb-30">
                                        <div className="form-contact">
                                            <div className="row">
                                                <div className="col-lg-12 mb-3">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={isCustomAmount}
                                                            disabled={isView}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;

                                                                setIsCustomAmount(checked);

                                                                if (checked && selectedStudent.length > 0) {
                                                                    const firstStudent = students.find(
                                                                        student => student.Id === selectedStudent[0]
                                                                    );

                                                                    if (firstStudent?.Class_Duration) {
                                                                        const validStudents = selectedStudent.filter(studentId => {
                                                                            const student = students.find(
                                                                                s => s.Id === studentId
                                                                            );

                                                                            return (
                                                                                Number(student?.Class_Duration) ===
                                                                                Number(firstStudent.Class_Duration)
                                                                            );
                                                                        });

                                                                        setSelectedStudent(validStudents);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <label className="form-check-label">
                                                            Custom Amount?
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-3">
                                                    <div className="form-group">
                                                        <label>Select Student*</label>
                                                        <Select
                                                            isMulti={isCreate}
                                                            components={isCreate ? { Option } : {}}
                                                            closeMenuOnSelect={false}
                                                            blurInputOnSelect={false}
                                                            hideSelectedOptions={false}
                                                            filterOption={filterStudentOption}
                                                            value={
                                                                !isCreate
                                                                    ? studentOptions.find(option => option.value === selectedStudent[0]) || null
                                                                    : studentOptions.filter(option =>
                                                                        selectedStudent.includes(option.value)
                                                                    )
                                                            }
                                                            isDisabled={isView || isEdit}
                                                            onChange={(selectedOptions) => {
                                                                if (isCreate) {
                                                                    const values = selectedOptions
                                                                        ? selectedOptions.map(o => o.value)
                                                                        : [];

                                                                    setSelectedStudent(values);

                                                                    if (values.length === 1) {
                                                                        setMultiPayments([]);

                                                                        getStudentDetails(values[0]);
                                                                        getStudentPaymentDetails(values[0]);
                                                                    } else if (values.length > 1) {
                                                                        getStudentFullDetails(values);
                                                                    }
                                                                } else {
                                                                    const value = selectedOptions
                                                                        ? selectedOptions.value
                                                                        : null;

                                                                    setSelectedStudent(value ? [value] : []);

                                                                    if (value) {
                                                                        getStudentDetails(value);
                                                                        getStudentPaymentDetails(value);
                                                                    }
                                                                }
                                                            }}
                                                            required
                                                            isClearable={!isView && !isEdit}
                                                            options={studentOptions}
                                                            classNamePrefix="select2-selection"
                                                            placeholder="Select Student"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-3">
                                                    <label>Payment Date*</label>
                                                    <div className="input-group">
                                                        <Flatpickr
                                                            className="form-control d-block"
                                                            placeholder="dd-mm-yyyy"
                                                            value={paymentDate}
                                                            disabled={isView}
                                                            options={{
                                                                dateFormat: "d-m-Y"
                                                            }}
                                                            onChange={(selectedDates, dateStr) => {
                                                                setPaymentDate(dateStr);
                                                            }}
                                                            required
                                                        />
                                                        <div className="input-group-append">
                                                            <span className="input-group-text">
                                                                <i className="mdi mdi-calendar" />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {dateError && <p className="text-danger">{dateError}</p>}
                                                </div>
                                                {isCustomAmount && (
                                                    <div className="col-lg-6 mb-3">
                                                        <div className="form-group">
                                                            <label>Total Amount*</label>
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                className="form-control"
                                                                value={studentPayment}
                                                                disabled={isView}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    setStudentPayment(value);
                                                                }}
                                                                placeholder="Total Amount"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="col-lg-6 mb-3">
                                                    <label>Payment Mode*</label>
                                                    <Select
                                                        value={
                                                            paymentOptions.find(
                                                                option => option.value === paymentMode
                                                            ) || null
                                                        }
                                                        onChange={(selectedOption) =>
                                                            setPaymentMode(selectedOption ? selectedOption.value : "")
                                                        }
                                                        isClearable
                                                        isDisabled={isView}
                                                        options={paymentOptions}
                                                        classNamePrefix="select2-selection"
                                                        placeholder="Select Payment Mode"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-lg-12 mb-3">
                                                    <div className="form-group">
                                                        <label>Notes</label>
                                                        <textarea
                                                            className="form-control"
                                                            rows={3}
                                                            style={{ resize: "vertical" }}
                                                            value={notes}
                                                            disabled={isView}
                                                            onChange={(e) => setNotes(e.target.value)}
                                                            placeholder="Notes"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 mb-3">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={isFeesPending}
                                                            disabled={isView}
                                                            onChange={(e) => setIsFeesPending(e.target.checked)}
                                                        />
                                                        <label className="form-check-label">
                                                            Is Fees Pending?
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Save/Cancel Button */}
                            {!isView && (
                                <div className="mt-3 d-flex gap-2 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </button>

                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {isCreate ? "Save" : "Update"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default AddEditPayment;
